<template>
    <basic-card>
        <template slot="header">
            {{displayName}}
        </template>
        <data-table
                :configuration="accountsTableConfig"
                :data="accounts"
                :default-sort="currentSort"
                @sortChanged="handleSortChanged"
                :component-id="`dashboard-widget-balances-[${displayName}]-details`"
        />
        <br/>
        <h1>Summary</h1>
        <data-table
                :configuration="summaryTableConfig"
                :data="summaryData"
                :component-id="`dashboard-widget-balances-[${displayName}]-summary`"
        />
    </basic-card>
</template>

<script>
    import BasicCard from "../../layout/BasicCard";
    import DataTable from "../../table/DataTable";
    import {mapGetters} from "vuex";
    import BankingStore from "../../../stores/banking-store";

    const totalRow = (d) => ({'background-color':'rgb(67,77,88)', 'font-weight': 'bold', 'color': '#9CA8B3 !important'})

    export default {
        name: "AccountBalancesOverviewWidget",
        components: {DataTable, BasicCard},
        props: {
            displayName: String,
            accountsToShow: String,
            defaultSortingField: String,
            defaultSortingDirection: String
        },
        methods: {
            handleSortChanged(sorting) {
                this.$set(this, 'currentSort', sorting);
            }
        },
        data() {
            return {
                currentSort: {sortField: this.defaultSortingField, direction: this.defaultSortingDirection},
                summaryTableConfig: {
                    objectName: "Summary",
                    columns: [
                        {
                            caption: "Currency",
                            datum: d => d.currency,
                            style: (d) => ({'text-align': 'left', ... d.currency == 'Total' ? totalRow(d) : undefined })
                        },
                        {
                            caption: "Balance in currency",
                            datum: d => d.currency == 'Total' ? '' : this.$formats.formatAmount(d.balance),
                            style: (d) => ({'text-align': 'right', ... d.currency == 'Total' ? totalRow(d) : undefined })
                        },
                        {
                            caption: "Balance in CZK",
                            datum: d => this.$formats.formatAmount(d.balancePrimaryCcy),
                            style: (d) => ({'text-align': 'right', ... d.currency == 'Total' ? totalRow(d) : undefined })
                        }
                    ]
                },
                accountsTableConfig: {
                    objectName: "Account",
                    columns: [
                        {
                            caption: "Owner",
                            datum: d => {
                                let owner = this.accountOwnerships.filter(accOwnership => accOwnership.accountId === d.id);
                                if(owner.length == 0) return "N/A";
                                else return owner[0].partyReference.displayName
                            },
                            sortField: "owner",
                            allowSort: true,
                            style: (d) => ({'white-space': 'nowrap'}),
                        },
                        {
                            caption: "Name",
                            datum: d => d.name,
                            style: (d) => ({'text-align': 'left'}),
                            sortField: "name",
                            allowSort: true
                        },
                        {
                            caption: "Last balance date",
                            datum: d => this.accountBalances(d)?.length ?? 0 > 0 ? new Date(this.accountBalances(d)[0].balanceDate) : new Date(0),
                            datumFormat: d => d.getTime() == 0 ? "N/A" : d.toLocaleDateString(),
                            style: (d) => ({'text-align': 'left'}),
                            allowSort: true,
                            sortField: "balanceDate",
                            sortType: "date"
                        },
                        {
                            caption: "CCY",
                            datum: d => d.currency,
                            style: (d) => ({'text-align': 'left'}),
                            sortField: "currency",
                            allowSort: true
                        },
                        {
                            caption: "Actual balance",
                            datum: d => typeof this.accountBalances(d) != 'undefined' && this.accountBalances(d).length > 0 ? this.accountBalances(d)[0].endBalance / 100 : 0,
                            datumFormat: d => this.$formats.formatAmount(d),
                            style: (d) => ({'text-align': 'right'}),
                            sortField: "actualBalance",
                            sortType: "number",
                            allowSort: true
                        }
                    ]
                }
            }
        },
        created() {
            if (this.$store._modules.root._children[this.displayName] == undefined) {
                this.$store.registerModule(this.displayName, BankingStore());
            }
            this.$store.dispatch(`accountQueries/refreshAccountQueries`)
            .then(() => {
                let fullQuery = this.$store.getters[`accountQueries/queryById`](this.accountsToShow);
                this.$store.dispatch(`${this.displayName}/refreshAccountsPaged`, {page: 0, size: 500, query: fullQuery})
                    .then(() => {
                        this.$forceUpdate();
                    })
            })

        },
        computed: {
            ...mapGetters({
                accountOwnerships: "accountOwnerships/accountOwnerships",
                accountBalances: 'banking/accountBalances',
                getValuesForCurrency: "currencies/getValuesForCurrency"
            }),
            accounts() {
                let accs = this.$store.getters[`${this.displayName}/accounts`];
                if(this.currentSort?.sortField) {
                    return this.$tableSort(this.currentSort, accs, this.accountsTableConfig)
                }
                return accs;
            },
            summaryData() {
                let result = {Total: {balancePrimaryCcy: 0}};
                let internalAccounts = this.accounts ?? [];
                let balances = internalAccounts
                    .filter(acc => this.accountBalances(acc) != undefined && this.accountBalances(acc).length > 0)
                    .map(acc => ({
                        currency: acc.currency,
                        balance: this.accountBalances(acc)[0],
                        rate: this.getValuesForCurrency(acc.currency)[0] == undefined ? 1 : this.getValuesForCurrency(acc.currency)[this.getValuesForCurrency(acc.currency).length - 1].value / 1000
                    }))
                    .reduce((prev, curr) => {
                        if (prev[curr.currency] == undefined) {
                            prev[curr.currency] = {balance: 0, balancePrimaryCcy: 0}
                        }
                        prev[curr.currency].balance += curr.balance.endBalance / 100;
                        prev[curr.currency].balancePrimaryCcy += (curr.balance.endBalance* curr.rate) / 100;
                        prev.Total.balancePrimaryCcy += (curr.balance.endBalance * curr.rate) / 100;

                        return prev;
                    }, result)
                let resultArray = Object.keys(result).filter(key => key != 'Total').map(key => ({currency: key, ...result[key]}));
                resultArray.push({currency: 'Total', ...result['Total']})

                return resultArray;
            }
        }
    }
</script>

<style scoped>

</style>